import React, { useEffect, useState } from "react";
import { Button, Input, Select, DatePicker, Upload, Row, Col, Form, Card, Tooltip } from "antd";
import { PlusOutlined, DeleteOutlined, FileExcelOutlined, FileWordOutlined, FileOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ErrorToast, SuccessToast } from "../../../widgets/AppToast";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";

const { Option } = Select;
const { TextArea } = Input;

// Accepted file extensions
const ACCEPTED_EXTENSIONS = ".xlsx,.xls,.doc,.docx";

// Returns an icon component based on the file name
const getFileIcon = (fileName = "") => {
    const ext = fileName.split(".").pop().toLowerCase();
    if (["xlsx", "xls"].includes(ext)) {
        return <FileExcelOutlined style={{ fontSize: "48px", color: "#217346" }} />;
    }
    if (["doc", "docx"].includes(ext)) {
        return <FileWordOutlined style={{ fontSize: "48px", color: "#2b579a" }} />;
    }
    return <FileOutlined style={{ fontSize: "48px", color: "#8c8c8c" }} />;
};

const PhotographicAnalysis = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [loader, setLoader] = useState(false);
    const [ships, setShips] = useState([]);
    const [zones, setZones] = useState([]);

    // Each entry: { uid, name, originFileObj }
    const [fileList, setFileList] = useState([]);

    // Notes keyed by file uid
    const [fileNotes, setFileNotes] = useState({});

    useEffect(() => {
        fetchShips();
        fetchZones();
    }, []);

    const fetchShips = async () => {
        try {
            const FORM_DATA = new FormData();
            const res = await post(ApiURL.list_ships_view_com, FORM_DATA);
            if (res?.status === true) {
                setShips(res?.ships || []);
            }
        } catch (error) {
            console.error("Error fetching ships:", error);
        }
    };

    const fetchZones = async () => {
        try {
            const FORM_DATA = new FormData();
            const res = await post(ApiURL.list_inspection_zones, FORM_DATA);
            if (res?.status === true) {
                const groupedZones = res?.data || {};
                const flatZones = Object.values(groupedZones).flat();
                setZones(flatZones);
            }
        } catch (error) {
            console.error("Error fetching zones:", error);
        }
    };

    // Handle file selection — only keep the single latest file
    const handleFileChange = ({ file, fileList: newFileList }) => {
        const latest = newFileList[newFileList.length - 1];
        if (!latest) {
            setFileList([]);
            return;
        }
        setFileList([{
            uid: latest.uid,
            name: latest.name || latest.originFileObj?.name || "file",
            originFileObj: latest.originFileObj,
        }]);
    };

    const handleNoteChange = (uid, note) => {
        setFileNotes((prev) => ({ ...prev, [uid]: note }));
    };

    const removeFile = (uid) => {
        setFileList((prev) => prev.filter((item) => item.uid !== uid));
        setFileNotes((prev) => {
            const updated = { ...prev };
            delete updated[uid];
            return updated;
        });
    };

    const onFinish = async (values) => {
        if (fileList.length === 0) {
            ErrorToast("Validation Error", "Please upload at least one document.");
            return;
        }

        setLoader(true);

        try {
            const FORM_DATA = new FormData();
            const user_Data = JSON.parse(localStorage.getItem("user_data"));

            FORM_DATA.append("ship_id", values.ship_id);
            FORM_DATA.append("title", values.title);
            FORM_DATA.append("inspection_date", values.inspection_date.format("YYYY-MM-DD"));
            FORM_DATA.append("zone_id", values.zone_id);
            FORM_DATA.append("inspection_type", values.inspection_type || "");
            FORM_DATA.append("inspector_name", values.inspector_name || "");
            FORM_DATA.append("user_notes", values.user_notes || "");
            FORM_DATA.append("company_id", user_Data?.data?.company?.company_id);
            FORM_DATA.append("user_id", user_Data?.data?.user_id);

            // Append each document using the backend key "document"
            fileList.forEach((file, index) => {
                const rawFile = file.originFileObj;
                if (rawFile) {
                    FORM_DATA.append("document", rawFile, file.name);
                    FORM_DATA.append(`user_note_${index}`, fileNotes[file.uid] || "");
                }
            });

            const res = await post(ApiURL.add_photographic_analysis, FORM_DATA);
            if (res?.status === true) {
                SuccessToast("Success", "Photographic Analysis detail added successfully!");
                navigate("/photographic-analysis");
            } else {
                ErrorToast("Error", res?.message || "Failed to add photographic analysis.");
            }
        } catch (error) {
            console.error("Submission error:", error);
            ErrorToast("Error", "Something went wrong during submission.");
        } finally {
            setLoader(false);
        }
    };

    return (
        <div className="white-container" style={{ padding: "10px" }}>
            <div className="row" style={{ padding: "10px", marginBottom: "10px" }}>
                <div className="col-6">
                    <div className="theme-content-title text-left">
                        <h3 style={{ margin: 0 }}>Photographic Analysis</h3>
                    </div>
                </div>
                <div className="col-6" style={{ textAlign: "right" }}>
                    <Button onClick={() => navigate("/photographic-analysis")}>Back to List</Button>
                </div>
            </div>

            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                initialValues={{ inspection_date: dayjs() }}
            >
                {/* ── Inspection Details ── */}
                <Card
                    title={<span style={{ fontWeight: 600 }}>Inspection Details</span>}
                    style={{ marginBottom: "20px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                >
                    <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="ship_id"
                                label="Select Ship"
                                rules={[{ required: true, message: "Please select a ship" }]}
                            >
                                <Select
                                    placeholder="Select Ship"
                                    options={ships.map((ship) => ({ label: ship.vessel_name, value: ship.id }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="title"
                                label="Title"
                                rules={[{ required: true, message: "Please enter a title" }]}
                            >
                                <Input placeholder="Enter title" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="inspection_date"
                                label="Inspection Date"
                                rules={[{ required: true, message: "Please select inspection date" }]}
                            >
                                <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={[24, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item
                                name="zone_id"
                                label="Inspection Zone"
                                rules={[{ required: true, message: "Please select an inspection zone" }]}
                            >
                                <Select
                                    placeholder="Select Zone"
                                    options={zones.map((zone) => ({ label: zone.name, value: zone.id }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="inspection_type" label="Inspection Type">
                                <Select placeholder="Select Inspection Type">
                                    <Option value="routine">Routine</Option>
                                    <Option value="pre_voyage">Pre Voyage</Option>
                                    <Option value="post_voyage">Post Voyage</Option>
                                    <Option value="annual">Annual</Option>
                                    <Option value="special">Special</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Form.Item name="inspector_name" label="Inspector Name">
                                <Input placeholder="Enter inspector name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={24}>
                        <Col span={24}>
                            <Form.Item name="user_notes" label="General User Notes">
                                <TextArea rows={3} placeholder="Enter general notes here..." />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* ── Documents & Notes ── */}
                <Card
                    title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span style={{ fontWeight: 600 }}>Observation Documents &amp; Notes</span>
                            <Upload
                                accept={ACCEPTED_EXTENSIONS}
                                showUploadList={false}
                                fileList={fileList}
                                onChange={handleFileChange}
                                beforeUpload={() => false}
                            >
                                <Button type="primary" icon={<PlusOutlined />} className="save_btn">
                                    {fileList.length > 0 ? "Replace Document" : "Add Document"}
                                </Button>
                            </Upload>
                        </div>
                    }
                    style={{ marginBottom: "20px", borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}
                >
                    <Row gutter={[24, 24]}>
                        {fileList.map((file, index) => (
                            <Col xs={24} lg={12} key={file.uid}>
                                <div
                                    style={{
                                        display: "flex",
                                        padding: "12px",
                                        border: "1px solid #f0f0f0",
                                        borderRadius: "8px",
                                        background: "#fff",
                                        height: "100%",
                                        transition: "all 0.3s ease",
                                    }}
                                    className="observation-card"
                                >
                                    {/* File icon area */}
                                    <div
                                        style={{
                                            width: "120px",
                                            height: "120px",
                                            flexShrink: 0,
                                            marginRight: "16px",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            borderRadius: "4px",
                                            background: "#fafafa",
                                            border: "1px solid #f0f0f0",
                                            padding: "8px",
                                            gap: "8px",
                                        }}
                                    >
                                        {getFileIcon(file.name)}
                                        <span
                                            style={{
                                                fontSize: "11px",
                                                color: "#595959",
                                                textAlign: "center",
                                                wordBreak: "break-all",
                                                maxWidth: "100px",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            title={file.name}
                                        >
                                            {file.name}
                                        </span>
                                    </div>

                                    {/* Note + actions */}
                                    <div
                                        style={{
                                            flex: 1,
                                            display: "flex",
                                            flexDirection: "column",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <TextArea
                                            placeholder="Add observation note..."
                                            autoSize={{ minRows: 3, maxRows: 4 }}
                                            value={fileNotes[file.uid] || ""}
                                            onChange={(e) => handleNoteChange(file.uid, e.target.value)}
                                            style={{
                                                marginBottom: "12px",
                                                fontSize: "14px",
                                                border: "1px solid #d9d9d9",
                                                borderRadius: "4px",
                                            }}
                                        />
                                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                            <Tooltip title="Remove">
                                                <Button
                                                    size="small"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => removeFile(file.uid)}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                </div>
                            </Col>
                        ))}

                        {fileList.length === 0 && (
                            <Col span={24}>
                                <div
                                    style={{
                                        textAlign: "center",
                                        padding: "60px 20px",
                                        background: "#fafafa",
                                        border: "1px dashed #d9d9d9",
                                        borderRadius: "8px",
                                        color: "#8c8c8c",
                                    }}
                                >
                                    <PlusOutlined style={{ fontSize: "24px", marginBottom: "8px" }} />
                                    <p style={{ margin: 0 }}>
                                        No document added yet. Click "Add Document" to upload an Excel or Word file.
                                    </p>
                                </div>
                            </Col>
                        )}
                    </Row>
                </Card>

                <div style={{ textAlign: "center", marginTop: "40px", marginBottom: "30px" }}>
                    <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        loading={loader}
                        className="save_btn"
                        style={{
                            height: "48px",
                            padding: "0 80px",
                            borderRadius: "24px",
                            fontWeight: 600,
                            fontSize: "16px",
                        }}
                    >
                        Submit Analysis
                    </Button>
                </div>
            </Form>
        </div>
    );
};

export default PhotographicAnalysis;
